import { z } from "zod";
import { Id } from "../common/ids.js";
import { PageQuery, page } from "../common/pagination.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { InquiryStatus } from "./inquiry.routes.js";
import { OrderStatus, OrderSummary, OrderView } from "./order.view.js";
import { TradeSide } from "./terms.js";
/** Orders: reading, cancelling, ready to ship, and the dashboards (ORD-2, ORD-4, WEB-5, card 054). */
/**
 * One step in an order's history as either party may see it (AUD-1): the audit events of the order, its quote
 * versions, and its inquiry, each reduced to what both sides are shown. People are not named; the caller's own steps
 * are marked.
 */
export const OrderTimelineEntry = z
    .object({
    at: Timestamp,
    action: z.enum([
        "inquiry.created",
        "inquiry.declined",
        "inquiry.withdrawn",
        "inquiry.expired",
        "quote.issued",
        "quote.countered",
        "quote.accepted",
        "quote.expired",
        "order.created",
        "order.payment_due",
        "order.ready_to_ship",
        "order.shipment_recorded",
        "order.shipment_corrected",
        "order.shipment_delivered",
        "order.delivered",
        "order.receipt_confirmed",
        "order.change_proposed",
        "order.change_accepted",
        "order.change_declined",
        "order.change_withdrawn",
        "order.transfer_reported",
        "order.transfer_confirmed",
        "order.transfer_rejected",
        "order.cancellation_requested",
        "order.cancellation_accepted",
        "order.cancellation_declined",
        "order.cancelled",
    ]),
    actor: z.enum(["buyer", "seller", "platform"]),
    byMe: z.boolean(),
    details: z
        .record(z.string(), z.string())
        .describe("Such as `versionNo`, `number`, `reason`, `seq`, or `mode`, by action"),
})
    .meta({ id: "OrderTimelineEntry" });
/** What an order waits for, as the home screens count it; each is also a filter of the order list (WEB-5, card 077). */
export const OrderWaiting = z
    .enum(["payment", "transfer_confirmation", "ready_to_ship", "cancellation_answer"])
    .meta({
    id: "OrderWaiting",
    description: "`payment`: an item due now, with no transfer waiting for the seller's answer. `transfer_confirmation`: a reported transfer waiting for the seller's answer. `ready_to_ship`: paid, not yet marked ready to ship. `cancellation_answer`: the other side asked to cancel, and this side answers",
});
/** Counts behind a home screen; each matches a filter of the list it links to (WEB-5). */
export const DashboardCounts = z
    .object({
    inquiries: z.record(InquiryStatus, z.number().int().nonnegative()).describe("By status (`status=`)"),
    orders: z.record(OrderStatus, z.number().int().nonnegative()).describe("By status (`status=`)"),
    paymentsDue: z.number().int().nonnegative().describe("Orders with a payment to make (`waiting=payment`)"),
    transfersToConfirm: z
        .number()
        .int()
        .nonnegative()
        .describe("Orders with a reported transfer to confirm (`waiting=transfer_confirmation`)"),
    readyToShip: z
        .number()
        .int()
        .nonnegative()
        .describe("Paid orders not yet marked ready to ship (`waiting=ready_to_ship`)"),
    cancellationRequestsToAnswer: z
        .number()
        .int()
        .nonnegative()
        .describe("Orders whose other side asked to cancel (`waiting=cancellation_answer`)"),
})
    .meta({ id: "DashboardCounts" });
const Party = { surface: "tenant", auth: "user" };
const OrderId = z.object({ orderId: Id });
const Answer = z.object({ orderId: Id, requestId: Id });
const Reason = z.string().trim().min(3).max(500);
export const listOrders = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.orders.list",
    method: "GET",
    path: "/v1/orders",
    marks: ["tenant"],
    summary: "The enterprise's orders as buyer, seller, or both, newest first, optionally in one status and waiting for one thing; each home count is one of these filters",
    prd: ["ORD-1", "WEB-5"],
    request: {
        query: PageQuery.extend({
            side: TradeSide.optional(),
            status: OrderStatus.optional(),
            waiting: OrderWaiting.optional(),
        }),
    },
    responses: { 200: { description: "Orders", schema: page(OrderSummary) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const getOrder = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.orders.get",
    method: "GET",
    path: "/v1/orders/{orderId}",
    marks: ["tenant"],
    summary: "An order with lines, schedule, FX snapshot, addresses, windows, protection, its latest cancellation request, and what the caller may do now",
    prd: ["ORD-1"],
    request: { params: OrderId },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const getOrderTimeline = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.orders.timeline",
    method: "GET",
    path: "/v1/orders/{orderId}/timeline",
    marks: ["tenant"],
    summary: "The deal's history from the inquiry on, oldest first, as either party may see it",
    prd: ["AUD-1"],
    request: { params: OrderId },
    responses: {
        200: { description: "The timeline", schema: z.object({ items: z.array(OrderTimelineEntry) }) },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const cancelOrder = defineRoute({
    ...Party,
    permission: "order:cancel",
    id: "trade.orders.cancel",
    method: "POST",
    path: "/v1/orders/{orderId}/cancel",
    marks: ["tenant", "idem", "if-match"],
    summary: "Either side cancels before payment, at once. After payment and before shipment it opens a cancellation request the other side answers; later, only after-sales can end the order",
    prd: ["ORD-4"],
    request: { params: OrderId, body: z.object({ reason: Reason }) },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const acceptCancellation = defineRoute({
    ...Party,
    permission: "order:cancel",
    id: "trade.orders.cancellation.accept",
    method: "POST",
    path: "/v1/orders/{orderId}/cancellation-requests/{requestId}/accept",
    marks: ["tenant", "idem"],
    summary: "The other side agrees and the order is cancelled. Items not yet paid are cancelled; items paid by bank transfer are recorded as refunded between the parties (ADR 0005), and held items are refunded through Payment",
    prd: ["ORD-4"],
    request: { params: Answer },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const declineCancellation = defineRoute({
    ...Party,
    permission: "order:cancel",
    id: "trade.orders.cancellation.decline",
    method: "POST",
    path: "/v1/orders/{orderId}/cancellation-requests/{requestId}/decline",
    marks: ["tenant", "idem"],
    summary: "The other side declines; the order goes on",
    prd: ["ORD-4"],
    request: { params: Answer },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const markReadyToShip = defineRoute({
    ...Party,
    permission: "shipment:manage",
    id: "trade.orders.ready_to_ship",
    method: "POST",
    path: "/v1/orders/{orderId}/ready-to-ship",
    marks: ["tenant", "idem"],
    summary: "The shop marks a paid order's goods ready, once; a balance due at ready to ship becomes due, with its payment window",
    prd: ["ORD-2"],
    request: { params: OrderId },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
/** One pro forma order sheet (ORD-6, card 060): the phase-1 contract for one version of the order. */
export const OrderDocumentView = z
    .object({
    id: Id,
    versionNo: z
        .number()
        .int()
        .positive()
        .describe("1 at creation, then one more for each accepted change order"),
    number: z.string().describe("Such as `PI-ORD-2026-000123-1`"),
    fileId: Id.describe("The PDF, downloaded through the files API"),
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .describe("Of the PDF's bytes, as issued"),
    issuedAt: Timestamp,
})
    .meta({ id: "OrderDocument" });
export const listOrderDocuments = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.orders.documents",
    method: "GET",
    path: "/v1/orders/{orderId}/documents",
    marks: ["tenant"],
    summary: "The order's pro forma order sheets, one per version, newest first: the PDF issued when the order was created and after each accepted change order, with its number and checksum. A version is listed once the worker has issued its PDF, moments after the version; download it through `GET /v1/files/{fileId}/download-url`",
    prd: ["ORD-6"],
    request: { params: OrderId },
    responses: { 200: { description: "Documents", schema: z.object({ items: z.array(OrderDocumentView) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const confirmReceipt = defineRoute({
    ...Party,
    permission: "receipt:confirm",
    id: "trade.orders.confirm_receipt",
    method: "POST",
    path: "/v1/orders/{orderId}/confirm-receipt",
    marks: ["tenant", "idem", "if-match"],
    summary: "The buyer confirms receipt of a delivered order, which completes it; `409 CASE_OPEN` while an after-sales case is open. Once the window ends, the platform confirms instead, as `timeout`",
    prd: ["ORD-3"],
    request: { params: OrderId },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "INVALID_TRANSITION",
        "CASE_OPEN",
        "VERSION_CONFLICT",
    ],
});
const dashboard = (side) => defineRoute({
    ...Party,
    permission: "order:read",
    id: `trade.dashboard.${side}`,
    method: "GET",
    path: `/v1/dashboard/${side}`,
    marks: ["tenant"],
    summary: `Counts for the ${side}'s home, each matching a filter of the inquiry or order list`,
    prd: ["WEB-5"],
    responses: { 200: { description: "Counts", schema: DashboardCounts } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const buyerDashboard = dashboard("buyer");
export const sellerDashboard = dashboard("seller");
export const orderRoutes = [
    listOrders,
    getOrder,
    getOrderTimeline,
    cancelOrder,
    acceptCancellation,
    declineCancellation,
    markReadyToShip,
    confirmReceipt,
    listOrderDocuments,
    buyerDashboard,
    sellerDashboard,
];
//# sourceMappingURL=order.routes.js.map